import { BasicResponseDTO } from '@dto/basicResponse.dto';
import { CreateResponseDTO } from '@dto/dashboard.dto';
import { AxiosError } from 'axios';
import { Log } from 'src/service/log.service';
import { deleteApi, postApi, putApi } from 'src/util/apiCalls';
import { GLOB } from "src/util/Glob";
import { CreatableType, HierarchyAccessors, HierarchyDomain, TreeCtx } from './hierarchyTypes';

// Toast + rethrow: the user gets feedback while the confirm dialog stays open for a retry.
const reportApiError = (action: string, reason: AxiosError<BasicResponseDTO>): never => {
  Log.error(reason.response?.data?.message ?? `Failed to ${action}!`, reason);
  throw reason;
};

// Depth-first lookup of the live node equal to `target` (menu may have been rebuilt since render).
export function getFresh<T>(target: T, nodes: T[] | undefined, acc: HierarchyAccessors<T>): T | undefined {
  if (!target || !nodes) return undefined;
  for (const node of nodes) {
    if (acc.equals(target, node)) return node;
    const found = getFresh(target, acc.getChildren(node), acc);
    if (found) return found;
  }
  return undefined;
}

export function hierarchySome<T>(nodes: readonly T[] | undefined, matches: (node: T) => boolean, getChildren: (node: T) => readonly T[] | undefined): boolean {
  return !!nodes?.some(node => matches(node) || hierarchySome(getChildren(node), matches, getChildren));
}


export function removeRecursive<T>(target: T, nodes: T[] | undefined, acc: HierarchyAccessors<T>): T[] {
  if (!nodes || !target) return nodes ?? [];
  return nodes
    .filter(node => !acc.equals(target, node))
    .map(node => {
      const kids = acc.getChildren(node);
      if (kids?.length) {
        const filtered = removeRecursive(target, kids, acc);
        kids.length = 0;
        kids.push(...filtered);
      }
      return node;
    });
}

export async function createNode<T extends object>(
  parent: T,
  type: CreatableType,
  rawName: string,
  cfg: { acc: HierarchyAccessors<T>; domain: HierarchyDomain; ctx: TreeCtx<T>; decorateChild?: (child: T) => void; onCreated?: (child: T | undefined, type: CreatableType) => void; },
): Promise<void> {
  const { acc, domain, ctx } = cfg;
  const name = rawName.trim();
  if (!name) {
    Log.warn('Name cannot be empty!');
    throw new Error('Name cannot be empty');
  }
  // Check duplicates against the live tree; a (possibly synthetic) root node has no
  // children of its own — its siblings are the top-level nodes.
  const liveParent = getFresh(parent, ctx.getNodes(), acc) ?? parent;
  const siblings = acc.getChildren(liveParent) ?? (acc.isRoot(liveParent) ? ctx.getNodes() : []);
  if (siblings.some(s => acc.getLabel(s) === name)) {
    Log.warn(`Name ${name} already exists inside ${acc.getLabel(parent)}`);
    throw new Error('Name already exists');
  }
  const payload = domain.buildCreatePayload({
    parentId: acc.getId(parent),
    groupId: acc.getGroupId(parent),
    isRoot: acc.isRoot(parent),
    isGroupRoot: acc.isGroupRoot(parent),
    name,
    type,
  });
  const resp = await postApi<CreateResponseDTO>(domain.createUrl(type), payload)
    .catch((reason: AxiosError<BasicResponseDTO>) => reportApiError(`create ${type} ${name}`, reason));
  const created = domain.parseCreated(resp.data.data);
  const fresh = getFresh(parent, ctx.getNodes(), acc);
  let child: T | undefined;
  if (fresh) {
    child = acc.makeChild(created, type, fresh, domain.childUrl?.(type, created.id));
    cfg.decorateChild?.(child);
    acc.insertChild(fresh, child);
    ctx.setNodes([...ctx.getNodes()]);
  } else if (acc.isRoot(parent) && acc.insertTopLevel) {
    // Synthetic root (e.g. the nav menu's virtual root) is absent from the tree —
    // insert at the top level so the node shows up before the reload round-trip.
    child = acc.makeChild(created, type, parent, domain.childUrl?.(type, created.id));
    cfg.decorateChild?.(child);
    const nodes = [...ctx.getNodes()];
    acc.insertTopLevel(nodes, child);
    ctx.setNodes(nodes);
  }
  await domain.reload();
  if (fresh && 'itemId' in fresh) GLOB.selectedItem.itemId === fresh.itemId && cfg.onCreated?.(child, type);
  else cfg.onCreated?.(child, type);
}

function findParent<T>(target: T, nodes: T[] | undefined, acc: HierarchyAccessors<T>): T | undefined {
  if (!target || !nodes) return undefined;
  for (const node of nodes) {
    const kids = acc.getChildren(node);
    if (kids?.some(k => acc.equals(target, k))) return node;
    const found = findParent(target, kids, acc);
    if (found) return found;
  }
  return undefined;
}

export async function renameNode<T>(
  node: T,
  type: string,
  rawName: string,
  cfg: { acc: HierarchyAccessors<T>; domain: HierarchyDomain; ctx: TreeCtx<T>; relabel: (live: T, label: string) => void; },
): Promise<void> {
  const { acc, domain, ctx, relabel } = cfg;
  const name = rawName.trim();
  if (!name) {
    Log.warn('Name cannot be empty!');
    throw new Error('Name cannot be empty');
  }
  const parent = findParent(node, ctx.getNodes(), acc);
  const siblings = parent ? acc.getChildren(parent) ?? [] : ctx.getNodes();
  if (siblings.some(s => !acc.equals(s, node) && acc.getLabel(s) === name)) {
    Log.warn(`Name ${name} already exists${parent ? ` inside ${acc.getLabel(parent)}` : ''}`);
    throw new Error('Name already exists');
  }
  await putApi<BasicResponseDTO, { label: string; }>(domain.modifyUrl(type, acc.getId(node)), { label: name })
    .catch((reason: AxiosError<BasicResponseDTO>) => reportApiError(`rename ${type}`, reason));
  const fresh = getFresh(node, ctx.getNodes(), acc);
  if (fresh) {
    relabel(fresh, name);
    ctx.setNodes([...ctx.getNodes()]);
  }
  void domain.reload();
}

export async function deleteNode<T>(
  node: T,
  type: string,
  cfg: { acc: HierarchyAccessors<T>; domain: HierarchyDomain; ctx: TreeCtx<T>; onDeleted?: (node: T) => void; },
): Promise<void> {
  const { acc, domain, ctx } = cfg;
  await deleteApi<BasicResponseDTO>(domain.modifyUrl(type, acc.getId(node)))
    .catch((reason: AxiosError<BasicResponseDTO>) => reportApiError(`delete ${type} ${acc.getLabel(node)}`, reason));
  cfg.onDeleted?.(node);
  ctx.setNodes(removeRecursive(node, ctx.getNodes(), acc));
  void domain.reload();
}
